import { Col, Row, Typography } from 'antd';
import React from 'react';
import { Link } from 'react-router';
import emailIllustration from '../assets/images/emails-amico@3x.png';
import { BoldSpanLargeSize } from './formSpans';
import { ThemedLogo } from './ThemedLogo';
import { CenterLogo } from './formLogo';
import { FormWrapper } from './formWrappers';

export type AuthEmailSuccessMode = 'signup' | 'resetPassword';

export interface AuthEmailSuccessLabels {
  logoAriaLabel: string;
  emailSentTitle: string;
  emailSentDescription1: string;
  emailSentDescription2: string;
  successTitle: string;
  successDescription1: string;
  successDescription2: string;
  spamTitle: string;
  spamDescription1: string;
  spamDescription2: string;
  backToLogin: string;
}

export const DEFAULT_AUTH_EMAIL_SUCCESS_LABELS: AuthEmailSuccessLabels = {
  logoAriaLabel: 'Home',
  emailSentTitle: 'Email successfully sent',
  emailSentDescription1:
    'We sent you an email with a link to reset your password.',
  emailSentDescription2:
    'Follow instructions in the email to set up a new password.',
  successTitle: 'Thank you for trying',
  successDescription1:
    'We will send you an email shortly with a link to activate your account.',
  successDescription2:
    'Follow instructions in the email to set up a password and get going.',
  spamTitle: 'Did not receive the mail?',
  spamDescription1: 'No problem... check your Spam / Junk folder.',
  spamDescription2:
    "If it's there, open the email and click the 'Not Spam' / 'Not Junk' button so future messages will come straight to your inbox.",
  backToLogin: 'Back to login',
};

export type AuthEmailSuccessProps = {
  mode: AuthEmailSuccessMode;
  logo: string;
  logoDark?: string;
  logoLinkTo: string;
  logoWidth?: string;
  /** Product name shown bold in signup title, e.g. Rows or generate.TAX */
  siteTitle?: string;
  labels?: Partial<AuthEmailSuccessLabels>;
};

export const AuthEmailSuccess: React.FC<AuthEmailSuccessProps> = ({
  mode,
  logo,
  logoDark,
  logoLinkTo,
  logoWidth,
  siteTitle = '',
  labels: labelsProp,
}) => {
  const labels = { ...DEFAULT_AUTH_EMAIL_SUCCESS_LABELS, ...labelsProp };
  const isResetPassword = mode === 'resetPassword';

  return (
    <FormWrapper>
      <Row justify="center">
        <Link to={logoLinkTo} aria-label={labels.logoAriaLabel}>
          <ThemedLogo src={logo} srcDark={logoDark} alt="" width={logoWidth} />
        </Link>
      </Row>
      <Row justify="center">
        <CenterLogo
          width="300px"
          height="300px"
          src={emailIllustration}
          alt=""
          aria-hidden
        />
      </Row>
      <Row justify="center">
        <Col
          span={12}
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Row justify="center">
            <Col
              span={24}
              style={{
                display: 'flex',
                justifyContent: 'center',
                textAlign: 'center',
                flexFlow: 'column',
                marginBottom: '25px',
              }}
            >
              <div>
                <Typography.Title
                  level={3}
                  style={{
                    fontWeight: 400,
                    minWidth: '400px',
                    color: 'var(--color-text-base, #2e2e2e)',
                  }}
                >
                  {isResetPassword ? (
                    labels.emailSentTitle
                  ) : (
                    <>
                      {labels.successTitle}{' '}
                      {siteTitle ? (
                        <BoldSpanLargeSize>{siteTitle}</BoldSpanLargeSize>
                      ) : null}
                    </>
                  )}
                </Typography.Title>
                <Typography.Text
                  style={{
                    fontWeight: 300,
                    fontSize: '16px',
                    minWidth: '624px',
                    color: 'var(--color-text-muted, #5a6a6a)',
                  }}
                >
                  {isResetPassword
                    ? labels.emailSentDescription1
                    : labels.successDescription1}
                  <br />
                  {isResetPassword
                    ? labels.emailSentDescription2
                    : labels.successDescription2}
                </Typography.Text>
              </div>
            </Col>
            <Col
              span={20}
              style={{
                backgroundColor: 'var(--color-surface-raised, #ffffff)',
                padding: '15px',
                border: '1px solid var(--color-border, #dbeae3)',
                borderRadius: '15px',
              }}
            >
              <Typography.Title
                level={4}
                style={{
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: '20px',
                  color: 'var(--color-text-dark, #042a2a)',
                  marginTop: 0,
                }}
              >
                {labels.spamTitle}
              </Typography.Title>
              <Typography.Text
                style={{
                  fontSize: '12px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  color: 'var(--color-text-muted, #5a6a6a)',
                }}
              >
                {`${labels.spamDescription1} ${labels.spamDescription2}`}
              </Typography.Text>
            </Col>
            <Col span={24} style={{ textAlign: 'center', marginTop: 20 }}>
              <Link className="link link-text" to={logoLinkTo}>
                {labels.backToLogin}
              </Link>
            </Col>
          </Row>
        </Col>
      </Row>
    </FormWrapper>
  );
};
